import React, {useEffect, useState} from "react";
import {Box, Button, CircularProgress, Typography} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import {LoginLayout} from "../components/layout/login_layout";
import {envVars} from "../components/utils/env_vars";
import {LOGIN_VIA_KEY, LOGIN_VIA_PENDIENTE_KEY, tryGoToDefaultPage, useAuthManager} from "../components/utils/auth";
import {useNavigate} from "react-router-dom";
import {useApiManager} from "../components/utils/api";
import {UserAuth, UserAuthItem, UserRestControllerApiFactory} from "arteco-api-client-ts";
import {AxiosResponse} from "axios";

// Cómo entró la última vez (#14004): «google» va directo al SSO de Google Workspace, «otra» a la
// pantalla de Logto (cuentas con contraseña). Solo se recuerda tras un login bueno (saveUser) y se
// olvida al cerrar sesión, para poder cambiar.
type LoginVia = "google" | "otra";

const readLoginVia = (): LoginVia | undefined => {
    try {
        const via = localStorage.getItem(LOGIN_VIA_KEY);
        return via === "google" || via === "otra" ? via : undefined;
    } catch {
        return undefined;
    }
};

const goToLogto = (via: LoginVia) => {
    try { localStorage.setItem(LOGIN_VIA_PENDIENTE_KEY, via); } catch {}
    window.location.href = envVars.apiBasePath + "/oauth2/authorization/logto" + (via === "google" ? "?via=google" : "");
};

export default function Login() {
    const authManager = useAuthManager();
    const apiManager = useApiManager();
    const navigate = useNavigate();
    const [choose, setChoose] = useState(false);

    useEffect(() => {

        const doRedirect = () => {
            const via = readLoginVia();
            if (via) {
                goToLogto(via);
            } else {
                setChoose(true);
            }
        };

        const handleRedirect = (auths: UserAuthItem[] | undefined) => {
            const redirectUrl = localStorage.getItem("login_redirect_url");
            if (redirectUrl) {
                localStorage.removeItem("login_redirect_url");
                navigate(redirectUrl);
            } else if (auths && auths.length > 0) {
                tryGoToDefaultPage(auths[0], authManager, navigate);
            } else {
                navigate("/app");
            }
        };

        if (authManager.isLogged()) {
            console.log("isLogged");
            handleRedirect(authManager.getAuthorities());
            return;
        }

        // Check if we can get user info (active session in backend)
        const api = apiManager.factory(UserRestControllerApiFactory);
        apiManager.executeRaw(api.info(), undefined, true).then((resp: AxiosResponse<UserAuth>) => {
            authManager.saveUser(resp.data);
            handleRedirect(resp.data.authorities);
        }).catch((reason) => {
            if (reason?.response?.status === 403) {
                // If the user is authenticated but rejected (403), log them out completely
                authManager.clear();
                navigate("/logout");
                return;
            }
            // 401 Unauthorized or any network error means they lack a session -> redirect to Logto
            doRedirect();
        });
    }, []);

    if (choose) {
        return (
            <LoginLayout title={"Iniciar sesión"}>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2, py: 3}}>
                    <Button variant="contained" size="large" startIcon={<GoogleIcon/>}
                            onClick={() => goToLogto("google")}>
                        Entrar con Google (Arteco)
                    </Button>
                    <Button variant="text" onClick={() => goToLogto("otra")}>
                        Otra cuenta
                    </Button>
                </Box>
            </LoginLayout>
        );
    }

    return (
        <LoginLayout title={"Redirigiendo..."}>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5}}>
                <CircularProgress size={60} sx={{mb: 3}}/>
                <Typography variant="body1">
                    Conectando con el sistema de autenticación...
                </Typography>
            </Box>
        </LoginLayout>
    );
}
